import React, { useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import servicesData from "../data/subservicesData"; // your service data
import bg3 from "../image/home img.png";

import AOS from "aos";
import "aos/dist/aos.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const ServicesPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const formRef = useRef(null);

  useEffect(() => {
    AOS.init({ duration: 900, once: true, easing: "ease-out-cubic" });
  }, []);

  useEffect(() => {
    if (location.state?.scrollToForm && formRef.current) {
      formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
      if (location.state.prefillService) {
        const select = formRef.current.querySelector("select[name='service']");
        if (select) select.value = location.state.prefillService;
      }
    }
  }, [location]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;
    const formData = new FormData(form);
    const data = Object.fromEntries(formData.entries());

    try {
      const response = await fetch(
        "https://kqc4n3pmfe.execute-api.ap-south-1.amazonaws.com/api/service/add",
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(data),
        }
      );

      let result;
      try {
        result = await response.json();
      } catch {
        toast.error("Server returned invalid data");
        return;
      }

      if (!response.ok) {
        toast.error(result.message || "Something went wrong");
        return;
      }

      toast.success(result.message || "Service request submitted successfully");
      form.reset();
    } catch {
      toast.error("Cannot reach server. Make sure backend is running!");
    }
  };

  return (
    <main className="w-full bg-white text-gray-800">

      {/* SEO */}
      <Helmet>
        <title>Our Services - Luxury Maintenance Solutions</title>
        <meta
          name="description"
          content="Explore our luxury maintenance services: MEP, HVAC, Smart Home, AMC and more. Premium solutions for your property."
        />
        <meta
          name="keywords"
          content="luxury maintenance, HVAC services, MEP solutions, smart home installation, AMC services, property maintenance"
        />
        <link rel="canonical" href="https://yourwebsite.com/services" />
      </Helmet>

      {/* HERO */}
      <section className="relative w-full h-[100vh] sm:h-[110vh] md:h-[120vh] lg:h-[130vh] overflow-hidden">
        <img
          src={bg3}
          alt="Luxury Property"
          className="absolute inset-0 w-full h-full object-cover brightness-100"
        />
        <div className="absolute inset-0 bg-white/30 flex flex-col justify-center items-center px-4 sm:px-6 text-center">
          <h1
            data-aos="fade-up"
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-cormorant tracking-wide mb-4 max-w-5xl text-gray-900"
          >
            <span className="block text-[#0a1f44] mt-2">
              We Don’t Just Offer Services —
            </span>
            <span className="block text-yellow-500 mt-2">
              We Solve Problems
            </span>
          </h1>
          <p
            data-aos="fade-up"
            data-aos-delay="200"
            className="text-gray-700 text-sm sm:text-base md:text-lg lg:text-xl max-w-xl"
          >
            Premium maintenance solutions tailored to your luxury property needs.
          </p>
        </div>
      </section>

      {/* SERVICES GRID */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 md:px-8 py-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
        {servicesData.map((service, index) => (
          <article
            key={service.slug || index}
            data-aos="fade-up"
            data-aos-delay={index * 100}
            onClick={() => navigate(`/services/${service.slug}`)}
            className="cursor-pointer bg-white/70 backdrop-blur-md rounded-3xl shadow-xl border border-yellow-300/20 overflow-hidden transform transition hover:scale-105 hover:shadow-2xl hover:border-yellow-400"
          >
            <img
              src={service.image}
              alt={`Service - ${service.title}`}
              loading="lazy"
              className="w-full h-56 object-cover rounded-t-3xl"
            />
            <div className="p-6 flex flex-col justify-center">
              <h2 className="text-xl md:text-2xl font-cormorant text-yellow-500 mb-2">
                {service.title}
              </h2>
              <p className="text-gray-700 text-sm md:text-base leading-relaxed">
                {service.description}
              </p>
            </div>
          </article>
        ))}
      </section>

      {/* REQUEST FORM */}
      <section
        ref={formRef}
        data-aos="fade-up"
        data-aos-delay="300"
        className="max-w-3xl mx-auto bg-white/80 backdrop-blur-md px-6 sm:px-8 py-10 rounded-3xl shadow-2xl border border-yellow-300/30"
      >
        <h2 className="text-2xl sm:text-3xl md:text-4xl font-cormorant text-yellow-500 mb-8 text-center">
          Request a Service
        </h2>
        <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <input
              type="text"
              name="name"
              placeholder="Your Name"
              required
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 placeholder:text-gray-400 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition"
            />
            <input
              type="email"
              name="email"
              placeholder="Your Email"
              required
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 placeholder:text-gray-400 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition"
            />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
            <input
              type="tel"
              name="phone"
              placeholder="Your Phone"
              required
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 placeholder:text-gray-400 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition"
            />
            <select
              name="service"
              required
              className="w-full border border-gray-300 rounded-xl px-4 sm:px-5 py-3 sm:py-4 focus:ring-2 focus:ring-yellow-500 focus:outline-none transition bg-white text-gray-800"
            >
              <option value="" disabled>
                Select Service
              </option>
              {servicesData.map((service, index) => (
                <option key={index} value={service.title}>
                  {service.title}
                </option>
              ))}
            </select>
          </div>
          <button
            type="submit"
            className="px-8 sm:px-12 py-3 sm:py-4 rounded-full text-sm sm:text-base font-medium text-white bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 hover:scale-105 hover:shadow-lg transition-transform duration-300"
          >
            Submit Request
          </button>
        </form>
      </section>

      <ToastContainer position="top-right" autoClose={3000} newestOnTop pauseOnHover theme="light" />
    </main>
  );
};

export { servicesData };
export default ServicesPage;
